/**
 * PATH: packages/tenant-shared/src/hooks/useApi.ts
 * FILE_ID: TENANTSHARED-HOOKS-USEAPI-001
 * PURPOSE: Single canonical axios instance + access-token plumbing for
 *          every tenant SPA on dashboard.quotech.ai. Lifted from the
 *          byte-identical apps/{newmark,sam}/src/shared/hooks/useApi.ts
 *          so the tenant tree, the @quotech/email-hub package, and the
 *          @quotech/settings-hub package all share ONE axios singleton
 *          with ONE in-memory access token.
 *
 * WHY:     ApiAuthGate.setAccessToken mutates the token slot in *this*
 *          module. If a feature package created its own axios instance,
 *          its requests would leave the Authorization header empty and
 *          the user would silently see 401s for some surfaces (e.g.
 *          settings-hub's `/api/email-prefs/*` calls) while other
 *          surfaces still worked. Hosting the axios instance once, in
 *          @quotech/tenant-shared, makes that class of bug structurally
 *          impossible: every consumer imports the SAME default export.
 *
 * FLOW:
 *   ┌──────────────────┐   ┌────────────────────────┐   ┌──────────────┐
 *   │ tenant boot      │ → │ ApiAuthGate writes the │ → │ packages +   │
 *   │ (apps/<t>/main)  │   │ token via setAccess-   │   │ tenant tree  │
 *   │                  │   │ Token() in this module │   │ both read it │
 *   └──────────────────┘   └────────────────────────┘   └──────────────┘
 *
 * RELATED:
 *   - apps/{newmark,sam}/src/shared/hooks/useApi.ts — thin shims that
 *     `export * from '@quotech/tenant-shared/hooks/useApi'` so the
 *     ~80 callers across the tenant tree don't have to be touched.
 *   - packages/settings-hub/src/_internal/runtime.ts — settings-hub's
 *     internal adapter re-exports the default `api` from this module.
 *   - packages/email-hub/src/_internal/hooks/useApi.ts — thin re-export
 *     shim since C4-MR6 (the pre-lift vendored copy is retired; its
 *     consumers share THIS instance + token slot).
 *   - apps/{newmark,sam}/src/shared/components/ApiAuthGate.tsx — calls
 *     setAccessToken/clearAccessToken on this module's singleton.
 */

import axios, { type InternalAxiosRequestConfig } from 'axios'
import type { AuthSessionPayload } from '../auth/types'
import {
  clearAuthV2CookieRefreshDead,
  isAuthV2CookieRefreshDead,
  markAuthV2CookieRefreshDead,
} from '../auth/authV2CookieRefreshDead'

/**
 * Defensive guard against a doubled `/api/api/...` URL prefix bug seen
 * on prod 2026-05-08 04:40 UTC (one stale browser session POSTed
 * `/sam-api/api/xero/payroll/sync/all` against dashboard.quotech.ai;
 * the qb nginx rewrite then turned it into `/api/api/xero/payroll/sync/all`
 * arriving at sam-api). Root cause was a stale bundle whose baked
 * `VITE_API_BASE` had `/api` accidentally appended to `/sam-api`.
 *
 * The current `apps/sam/.env.production` is correct (`/sam-api`) and
 * the `build-sam` CI job uses it unmodified, but a misbuild or a
 * misset env var on a future deploy could regress silently because
 * axios will happily concatenate. This sanitizer makes the bug
 * structurally impossible to ship by:
 *
 *   1. Stripping any trailing slashes (axios + path joining loathes them).
 *   2. Stripping a trailing `/api` segment when it appears RIGHT AFTER
 *      a tenant API prefix (`/sam-api/api` → `/sam-api`,
 *      `/newmark-api/api` → `/newmark-api`, etc.). The prefix shape is
 *      structural (`/<name>-api`), NOT a hardcoded tenant list — the
 *      old list silently missed `newmark` (and every future tenant),
 *      which is exactly the per-tenant-drift class Phase 5 of the
 *      2026-06 tenant convergence audit bans. We're deliberately
 *      conservative: a base of `/api` alone is left untouched (that's
 *      the legitimate dev / direct-Django-mount default).
 *
 * Idempotent. Logs a warning in dev so we notice the misconfig.
 */
export function sanitizeApiBase(raw: string): string {
  const trimmed = raw.replace(/\/+$/, '')
  const doubled = trimmed.match(/^(\/[a-z][a-z0-9-]*-api)\/api$/i)
  if (doubled) {
    if (typeof console !== 'undefined' && import.meta.env.DEV) {
      console.warn(
        `[useApi] VITE_API_BASE="${raw}" has a doubled /api segment after ` +
          `the tenant prefix. Auto-correcting to "${doubled[1]}". Fix the ` +
          `build env so this isn't relied on (see useApi.ts).`,
      )
    }
    return doubled[1]
  }
  return trimmed || '/api'
}

export const API_BASE = sanitizeApiBase(import.meta.env.VITE_API_BASE || '/api')
export const AUTH_REQUIRED_EVENT = 'api:auth-required'

let accessToken: string | null = null

export function getAccessToken(): string | null {
  return accessToken
}

export function setAccessToken(token: string): void {
  accessToken = token
}

export function clearAccessToken(): void {
  accessToken = null
}

/**
 * Tenant API bases look like `/newmark-api` / `/sam-api`. Master (and the
 * email-hub when embedded on dashboard.quotech.ai) uses `/api`.
 *
 * WHY this split matters for refresh (BUG-2026-07-25-AUTH-REFRESH-404-CASCADE):
 *   - Master Django has NO `POST /api/auth/refresh` (404). Live refresh is
 *     Auth v2 cookie `POST /api/auth/v2/refresh/`.
 *   - Tenant backends expose `GET /auth/refresh` (POST returns 405).
 * Hitting the wrong shape turns an ordinary access-token expiry into a
 * cascade of 401s and a stuck org switcher.
 */
export function isTenantApiBase(base: string): boolean {
  return /\/[a-z][a-z0-9-]*-api$/i.test(base.replace(/\/+$/, ''))
}

function applyRefreshedToken(
  payload: AuthSessionPayload | { access_token?: string | null },
): AuthSessionPayload | null {
  const token = payload?.access_token
  if (!token) return null
  setAccessToken(token)
  if ('authenticated' in payload && payload.authenticated) {
    return payload as AuthSessionPayload
  }
  return {
    auth_enabled: true,
    authenticated: true,
    access_token: token,
  }
}

export async function refreshAccessToken(): Promise<AuthSessionPayload | null> {
  try {
    if (isTenantApiBase(API_BASE)) {
      // Tenant FastAPI: session cookie → GET /auth/refresh (POST is 405).
      const response = await api.get<AuthSessionPayload>('/auth/refresh', {
        withCredentials: true,
      })
      return applyRefreshedToken(response.data ?? {})
    }
    // Master Django Auth v2: HttpOnly quotech_refresh cookie, path=/api/auth/v2/.
    // BUG-2026-08-03-AUTHV2-COOKIE-REFRESH-401-STORM: refuse re-POST after a
    // definitive dead latch (no JS-readable token to tombstone).
    if (isAuthV2CookieRefreshDead()) {
      return null
    }
    const response = await api.post<{ access_token?: string }>(
      '/auth/v2/refresh/',
      {},
      { withCredentials: true },
    )
    const applied = applyRefreshedToken(response.data ?? {})
    if (applied) clearAuthV2CookieRefreshDead()
    return applied
  } catch (err: unknown) {
    const status =
      err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { status?: number } }).response?.status
        : undefined
    if (status === 401 || status === 403) {
      markAuthV2CookieRefreshDead()
    }
    return null
  }
}

// Single-flight refresh. When several requests 401 at once (a page
// loads 5 GETs and the access token has just expired), they must share
// ONE refresh call and all replay after it resolves — otherwise
// we fire N refreshes and risk rotating the token out from under each
// other. Mirrors the `isRefreshing`/subscriber queue in @quotech/api's
// client.ts. The in-flight promise clears itself on settle so the next
// genuine expiry refreshes again.
let refreshInFlight: Promise<AuthSessionPayload | null> | null = null

export function refreshAccessTokenOnce(): Promise<AuthSessionPayload | null> {
  if (!refreshInFlight) {
    refreshInFlight = refreshAccessToken().finally(() => {
      refreshInFlight = null
    })
  }
  return refreshInFlight
}

const api = axios.create({ baseURL: API_BASE })

api.interceptors.request.use((config) => {
  const token = getAccessToken()
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Endpoints where a 401 is an *attempt* result, not a "session just expired"
// signal. Firing AUTH_REQUIRED_EVENT here would trigger ApiAuthGate.clearSession(),
// which sets localStorage.sso_suppressed='1' and silently skips SSO on every
// future page load. That's how users ended up permanently stuck on the
// "Sign in with Quotech" card: the SSO POST itself returned 401 (e.g. expired
// Quotech JWT or unknown org slug), the interceptor treated that as a
// de-auth event, and from then on the gate refused to even try SSO.
const AUTH_PROBE_PATHS = [
  '/auth/sso',
  '/auth/v2/refresh',
  '/auth/refresh',
  '/auth/session',
  '/auth/login',
]

function isAuthProbe(url: string | undefined): boolean {
  if (!url) return false
  return AUTH_PROBE_PATHS.some((p) => url.includes(p))
}

// BUG-0038: diagnostic/observability surfaces (notably the "Recent issues"
// integration-events poller) are not the source-of-truth for auth state.
// They fan out repeatedly while a tab is open (60s + visibility refetches +
// the drawer's own 30s while-open poll); if ONE 401 from this stream is
// allowed to wipe the in-memory access token AND dispatch
// AUTH_REQUIRED_EVENT, the gate logs the operator out + sets
// `sso_suppressed='1'` — the exact "stuck on Sign-in" trap documented for
// auth probes above. Tolerant paths preserve BOTH the token (other
// endpoints may still be 200ing) AND the session, and the calling hook is
// responsible for backing off its own polling on first 401 to stop the
// console spam. The carveout intentionally matches a prefix so query
// strings (`?limit=100`) and DELETE variants (`/integration-events?before_id=…`)
// both qualify without listing every shape.
//
// TOLERANT IS NOT "UNRECOVERABLE" (change #294). Tolerant used to `return`
// above the refresh-and-replay branch, so an ordinary mid-session access-token
// expiry — the thing every other endpoint survives silently — killed the
// Recent-issues panel for the rest of the tab and told the operator their
// ACCOUNT lacked read access (the endpoint only requires IsAuthenticated).
// Tolerant paths now take the same one refresh + one replay; only the terminal
// side-effects (clearAccessToken + AUTH_REQUIRED_EVENT) stay carved out, which
// is all this list was ever meant to protect.
const AUTH_TOLERANT_PATHS = ['/integration-events']

function isAuthTolerant(url: string | undefined): boolean {
  if (!url) return false
  return AUTH_TOLERANT_PATHS.some((p) => url.includes(p))
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error?.response?.status !== 401) {
      return Promise.reject(error)
    }

    const url: string | undefined = error?.config?.url
    const tolerant = isAuthTolerant(url)

    // LOGOUT-ON-DEPLOY race: every deploy ships content-hashed chunks, so
    // the running tab force-reloads (chunkErrorHandler / VersionWatcher).
    // That reload discards the in-memory bearer, and route data requests
    // fire BEFORE the gate's refreshSession()/`GET /auth/session` bootstrap
    // has re-minted it — so they 401 with NO bearer at all. That is not a
    // de-auth: the tenant cookie session is still valid and the gate will
    // recover it silently. Treating it as a de-auth here dispatches
    // AUTH_REQUIRED_EVENT → clearSession(), which (historically) set
    // sso_suppressed='1' and stranded the operator on the "Sign in with
    // Quotech" card after every update. Only react when we actually HELD a
    // bearer that the server rejected (a genuine mid-session expiry/revoke).
    if (getAccessToken() === null) {
      return Promise.reject(error)
    }

    // The /auth/{sso,refresh,session,login} calls themselves 401'd — never
    // try to refresh-then-retry those (the refresh call IS one of them, so
    // retrying would loop). Clear the dead bearer and reject WITHOUT
    // dispatching AUTH_REQUIRED_EVENT for a probe — doing so is the
    // "stuck on Sign in with Quotech" trap documented on isAuthProbe above.
    if (isAuthProbe(url)) {
      clearAccessToken()
      return Promise.reject(error)
    }

    // SEAMLESS MID-SESSION REFRESH (operator 2026-06-10: "account is
    // logging out after one day or after some hours … if access token
    // expires, fire refresh token api and replace the old access token
    // with the new, and call the get apis again for the screen the user
    // is present … so that the replacement of access token is seamless").
    //
    // We HELD a bearer that the server rejected → the SHORT-lived in-memory
    // access token expired mid-session. The tenant session (HttpOnly
    // refresh cookie, ~30 days) is almost certainly still alive, so try ONE
    // silent refresh and REPLAY the original request before treating this
    // as a logout. On success the caller's GET resolves as if the expiry
    // never happened (no flash to the sign-in card, no lost screen state).
    // `_retry` guards against an infinite loop if the replay also 401s.
    const original = error?.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined
    if (original && !original._retry) {
      original._retry = true
      const refreshed = await refreshAccessTokenOnce()
      if (refreshed?.access_token) {
        // The request interceptor reads getAccessToken() at send time,
        // which refreshAccessToken() has already updated, so the replay
        // automatically carries the freshly minted bearer.
        return api(original)
      }
    }

    // The replay path above is shared with tolerant paths; only the terminal
    // de-auth below is carved out. A diagnostics surface must never be the
    // thing that logs the operator out (BUG-0038).
    if (tolerant) {
      return Promise.reject(error)
    }

    // Refresh failed definitively (or we already retried once) → this is a
    // genuine de-auth. Clear the token and let ApiAuthGate take over.
    clearAccessToken()
    window.dispatchEvent(new Event(AUTH_REQUIRED_EVENT))
    return Promise.reject(error)
  },
)

export default api
